import React, { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  getAvailableSlotsForReassign,
  reassignCandidateSlot,
} from "../../api/assessmentslots.api";
import useApiToken from "../../hooks/useApiToken";

// Moves a candidate onto a different slot, replacing their existing booking. Distinct from
// ReassignSlotModal (used from the slot-candidates row): that one lets cancel and book happen
// independently. Here both halves always happen together - "replace this booking" is the single
// operation a recruiter wants from a candidate's profile.
//
// Same-assessment slots are shown by default and selected with a plain radio-style dropdown, so
// the common case (reschedule, keep the same test) can't accidentally drift onto a different
// assessment. Checking "Show slots for other assessments too" reveals every other active slot at
// this role status, each visibly labelled with its assessment name and highlighted when it
// differs from the current one - crossing that line is possible, but never silent.
//
// The heavy lifting is already done server-side by POST /AssessmentSlotStatus/reassign: it cancels
// the old booking (AdminStatus = -1), creates the new one stamped with ReassignedFromSlotId, clears
// the previous score/start/end so the candidate can actually sit the test again, and emails them
// the new slot details. It already supports a differing assessment - it looks up the
// RoleAssessmentMapping for the NEW slot's questionnaire and reattaches the score record to it.

interface AvailableSlot {
  id: string;
  startTime: string;
  endTime: string;
  assessmentName: string;
  assessmentQuestionnaireId: string | null;
  roleName: string;
  isActive: boolean;
}

interface RebookAssessmentSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  currentSlotId: string;
  currentSlotStartTime: string;
  roleStatusId: string;
  assessmentQuestionnaireId: string | null;
  assessmentName: string | null;
  onRebooked: () => void;
}

const RebookAssessmentSlotModal: React.FC<RebookAssessmentSlotModalProps> = ({
  isOpen,
  onClose,
  candidateId,
  candidateName,
  currentSlotId,
  currentSlotStartTime,
  roleStatusId,
  assessmentQuestionnaireId,
  assessmentName,
  onRebooked,
}) => {
  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [selectedSlotId, setSelectedSlotId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const token = useApiToken();

  useEffect(() => {
    if (!isOpen || !roleStatusId || !token) return;

    let cancelled = false;
    setLoading(true);
    setErr(null);
    setSelectedSlotId("");

    getAvailableSlotsForReassign(roleStatusId, token!)
      .then((data: AvailableSlot[]) => {
        if (cancelled) return;
        setSlots(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setErr("Could not load available slots.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, roleStatusId, token]);

  const sameAssessmentSlots = useMemo(
    () =>
      slots.filter((slot) => {
        if (slot.id === currentSlotId) return false;
        // Match on id, never on name - two questionnaires can share a title, and silently moving
        // a candidate onto a same-named-but-different assessment is the exact mistake the default
        // view exists to prevent. If the current slot has no questionnaire on record we cannot
        // prove a match, so we show nothing here rather than guess.
        if (!assessmentQuestionnaireId) return false;
        return slot.assessmentQuestionnaireId === assessmentQuestionnaireId;
      }),
    [slots, currentSlotId, assessmentQuestionnaireId]
  );

  // Every other active slot at this role status, any assessment. Shown alongside
  // sameAssessmentSlots (in its own optgroup below) so a recruiter always sees every option.
  const otherAssessmentSlots = useMemo(
    () => slots.filter((slot) => slot.id !== currentSlotId && !sameAssessmentSlots.includes(slot)),
    [slots, currentSlotId, sameAssessmentSlots]
  );

  const selectableSlots = [...sameAssessmentSlots, ...otherAssessmentSlots];

  const selectedSlot = selectableSlots.find((slot) => slot.id === selectedSlotId) ?? null;
  const isCrossAssessment =
    !!selectedSlot &&
    !!assessmentQuestionnaireId &&
    selectedSlot.assessmentQuestionnaireId !== assessmentQuestionnaireId;

  const formatDateTime = (dateString: string) => {
    const hasExplicitTz = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(dateString);
    const date = new Date(hasExplicitTz ? dateString : `${dateString}Z`);
    if (isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleConfirm = async () => {
    if (!selectedSlotId) return;
    setSubmitting(true);
    setErr(null);
    try {
      await reassignCandidateSlot(
        candidateId,
        currentSlotId,
        selectedSlotId,
        true, // always cancel the existing booking - this is a replacement, not an addition
        true,
        token!
      );
      onRebooked();
      onClose();
    } catch {
      setErr("Failed to rebook the slot. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative inline-block align-bottom bg-white dark:bg-darkbglight rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
            Book a Different Slot
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Move <span className="font-semibold">{candidateName}</span> to another slot. Their
            current booking will be replaced.
          </p>

          <div className="mt-4 rounded-md bg-gray-50 dark:bg-darkbg px-3 py-2 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-gray-500 dark:text-gray-400">Assessment</span>
              <span className="font-medium text-gray-900 dark:text-gray-200 text-right">
                {assessmentName || "Unknown"}
              </span>
            </div>
            <div className="flex justify-between gap-3 mt-1">
              <span className="text-gray-500 dark:text-gray-400">Current slot</span>
              <span className="font-medium text-gray-900 dark:text-gray-200 text-right">
                {formatDateTime(currentSlotStartTime)}
              </span>
            </div>
          </div>

          {err && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm">
              {err}
            </div>
          )}

          <div className="mt-4">
            <label
              htmlFor="rebookSlotSelect"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              New slot
            </label>
            {loading ? (
              <p className="mt-2 text-sm text-gray-500">Loading available slots...</p>
            ) : selectableSlots.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">No other upcoming slots exist at this stage.</p>
            ) : (
              <select
                id="rebookSlotSelect"
                value={selectedSlotId}
                onChange={(e) => setSelectedSlotId(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-darkbg dark:text-gray-300"
              >
                <option value="">Select a slot...</option>
                {/* Grouped so the same-assessment choices - the common case - are never buried
                    below a long list of other tests. */}
                {sameAssessmentSlots.length > 0 && (
                  <optgroup label={assessmentName ? `${assessmentName} (same assessment)` : "Same assessment"}>
                    {sameAssessmentSlots.map((slot) => (
                      <option key={slot.id} value={slot.id}>
                        {formatDateTime(slot.startTime)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {otherAssessmentSlots.length > 0 && (
                  <optgroup label="Other assessments">
                    {otherAssessmentSlots.map((slot) => (
                      <option key={slot.id} value={slot.id}>
                        {formatDateTime(slot.startTime)} — {slot.assessmentName}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
          </div>

          {selectedSlotId && (
            <div className="mt-4 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300">
              {isCrossAssessment && (
                <p className="font-semibold mb-2 text-amber-900 dark:text-amber-200">
                  This slot is for a different assessment ({selectedSlot?.assessmentName}), not{" "}
                  {assessmentName || "the current one"}. The candidate will take a different test.
                </p>
              )}
              <p className="font-semibold mb-1">This will:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Cancel the current slot booking</li>
                <li>
                  Clear any existing score for {isCrossAssessment ? "this role stage" : "this assessment"} so
                  it can be {isCrossAssessment ? "taken" : "retaken"}
                </li>
                <li>Email the candidate the new slot details with the SEB launch link</li>
              </ul>
              {/* The reassign endpoint always sends the SEB email and the new booking starts with
                  TakenWithoutSeb = false, so a previously-granted non-SEB exemption does not carry
                  over. Recruiters have to re-grant it or the candidate will be stuck needing SEB. */}
              <p className="mt-2">
                A non-SEB exemption does not carry over. If they need the plain link, use
                <span className="font-semibold"> Send Assessment Link Without SEB</span> again after
                rebooking.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting || !selectedSlotId}
              className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Rebooking..." : "Replace Slot"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RebookAssessmentSlotModal;
